/**
 * Custom hooks for My Tasks page table columns
 */
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from '@tanstack/react-router';
import type { AssignmentProcess, AssignmentProcessStepRecording } from '@bliks-io/contracts';
import type {
  ProcessStepAssignment,
  InProgressRecording,
  TaskProcess
} from '@/api/queries/useTasks';
import { useStandardRecordingTimeMs } from '@/hooks/useStandardRecordingTime';
import {
  Button,
  renderFallback,
  createUserCardColumn,
  createColumnHeader,
  createDateColumn,
  createDueDateColumn,
  type TombstoneLookup,
  type DataTableColumnDef
} from '@bliks-io/ui';
import { ProcessStepCell } from '../components/ProcessStepCell';
import type { ProcessStepInfo } from '../types';
import type { ProcessStepResult } from '../utils/processStepInfo';

export interface ColumnHelpersParams {
  slug: string;
  getProcessName: (process: string | AssignmentProcess) => string;
  getDepartment: (process: string | AssignmentProcess) => string;
  getProcessStepInfo: (
    step:
      | string
      | ProcessStepInfo
      | AssignmentProcessStepRecording
      | 'START'
      | ProcessStepAssignment['sourceProcessStep'],
    ruleIds?: string[],
    assignment?:
      | ProcessStepAssignment
      | { process: string | AssignmentProcess | Record<string, unknown> }
  ) => ProcessStepResult;
  setSelectedAssignment: (assignment: ProcessStepAssignment) => void;
  setSelectedReassignment: (assignment: ProcessStepAssignment) => void;
}

export function useOpenProcessesColumns(slug: string, userLookup?: TombstoneLookup) {
  const { t } = useTranslation();
  const standardRecordingTimeMs = useStandardRecordingTimeMs();

  return useMemo<DataTableColumnDef<TaskProcess>[]>(
    () => [
      {
        accessorKey: 'name',
        meta: { label: t('myTasks.process'), filterType: 'text' },
        header: createColumnHeader(t('myTasks.process')),
        cell: ({ row }) => (
          <Link
            to="/$slug/processes/all-processes/$processId"
            params={{ slug, processId: row.original._id }}
            className="font-medium hover:underline"
          >
            {row.getValue('name')}
          </Link>
        ),
        enableColumnFilter: true
      },
      {
        accessorKey: 'department',
        meta: { label: t('myTasks.department'), filterType: 'text' },
        header: createColumnHeader(t('myTasks.department')),
        cell: ({ row }) =>
          renderFallback(
            typeof row.original.department === 'object' && row.original.department
              ? row.original.department.name
              : row.original.department
          ),
        accessorFn: (row) =>
          typeof row.department === 'object' && row.department
            ? row.department.name
            : row.department,
        enableColumnFilter: true
      },
      createUserCardColumn<TaskProcess>('owner', t('myTasks.processOwner'), {
        variant: 'with-role',
        itemVariant: 'outline',
        enableFiltering: true,
        userLookup
      }),
      createDueDateColumn<TaskProcess>(t('myTasks.dueDate'), standardRecordingTimeMs),
      {
        id: 'actions',
        meta: { sticky: 'right' },
        header: () => <div className="sr-only">{t('common.Actions')}</div>,
        cell: ({ row }) => (
          <div className="flex justify-end">
            <Button size="small" variant="primary" asChild>
              <Link
                to="/$slug/processes/recording/start-recording/$processId"
                params={{ slug, processId: row.original._id }}
              >
                {t('myTasks.startRecording')}
              </Link>
            </Button>
          </div>
        ),
        enableSorting: false,
        enableHiding: false
      }
    ],
    [t, slug, standardRecordingTimeMs, userLookup]
  );
}

export function useInProgressRecordingsColumns(
  params: Pick<
    ColumnHelpersParams,
    'slug' | 'getProcessName' | 'getDepartment' | 'getProcessStepInfo'
  >
) {
  const { t } = useTranslation();
  const { slug, getProcessName, getDepartment, getProcessStepInfo } = params;
  const standardRecordingTimeMs = useStandardRecordingTimeMs();

  return useMemo<DataTableColumnDef<InProgressRecording>[]>(
    () => [
      {
        accessorKey: 'task',
        meta: { label: t('myTasks.processStep'), filterType: 'text' },
        header: createColumnHeader(t('myTasks.processStep')),
        cell: ({ row }) => {
          const recording = row.original;
          const step: ProcessStepInfo = {
            actionType: recording.actionType,
            task: recording.task
          };
          return <ProcessStepCell step={step} getStepInfo={getProcessStepInfo} />;
        },
        accessorFn: (row) => {
          const step: ProcessStepInfo = {
            actionType: row.actionType,
            task: row.task
          };
          const stepInfo = getProcessStepInfo(step);
          return stepInfo.label;
        },
        enableColumnFilter: true
      },
      {
        accessorKey: 'process',
        id: 'processName',
        meta: { label: t('myTasks.process'), filterType: 'text' },
        header: createColumnHeader(t('myTasks.process')),
        cell: ({ row }) => (
          <Link
            to="/$slug/processes/all-processes/$processId"
            params={{
              slug,
              processId:
                typeof row.original.process === 'string'
                  ? row.original.process
                  : row.original.process._id
            }}
            className="font-medium hover:underline"
          >
            {getProcessName(row.original.process)}
          </Link>
        ),
        accessorFn: (row) => getProcessName(row.process),
        enableColumnFilter: true
      },
      {
        id: 'department',
        meta: { label: t('myTasks.department'), filterType: 'text' },
        header: createColumnHeader(t('myTasks.department')),
        cell: ({ row }) => getDepartment(row.original.process),
        accessorFn: (row) => getDepartment(row.process),
        enableColumnFilter: true
      },
      createDateColumn<InProgressRecording>('createdAt', t('myTasks.startedDate'), {
        format: 'datetime'
      }),
      createDueDateColumn<InProgressRecording>(t('myTasks.dueDate'), standardRecordingTimeMs),
      {
        id: 'actions',
        meta: { sticky: 'right' },
        header: () => <div className="sr-only">{t('common.Actions')}</div>,
        cell: ({ row }) => (
          <div className="flex justify-end">
            <Button size="small" variant="primary" asChild>
              <Link
                to="/$slug/processes/recording/step-recording/$recordingId"
                params={{ slug, recordingId: row.original._id }}
              >
                {t('common.Continue')}
              </Link>
            </Button>
          </div>
        ),
        enableSorting: false,
        enableHiding: false
      }
    ],
    [t, slug, getProcessStepInfo, getProcessName, getDepartment, standardRecordingTimeMs]
  );
}

// These column hooks live in sibling files; re-exported here so the page
// imports all My-Tasks column hooks from one module.
export { useAssignmentsColumns } from './useAssignmentsColumns';
export { usePendingSurveysColumns } from './usePendingSurveysColumns';
